import { createOrders, getLastSync, getOrders, sync } from '@/adapters/order';
import { checkStocks } from '@/adapters/stock';
import CustomSearch from '@/components/search';
import { dateTimeFormat } from '@/configs/dateFormat';
import { getOrderFromName } from '@/helpers/order';
import {
  IBentoOrderItemCreate,
  IBentoOrderRes,
  IOrderFull,
  ISyncOrderBento,
} from '@/interfaces/order';
import { formatDate, formatNumber } from '@/utilities/dataUtils';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Flex, notification, Table, TableColumnsType } from 'antd';
import { debounce, sumBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

const OrderPage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selected, setSelected] = useState<IBentoOrderItemCreate[]>([]);

  const { data: lastSync, refetch: refreshSync } = useQuery<
    ISyncOrderBento,
    Error
  >({
    queryKey: ['sync-last-order'],
    queryFn: getLastSync,
    retry: 0,
  });

  const { data: orders, refetch } = useQuery<IBentoOrderRes[], Error>({
    queryKey: ['get-orders'],
    queryFn: getOrders,
    retry: 0,
  });

  const sycnOrder = useMutation({
    mutationFn: async () => await sync(),
    mutationKey: ['sync-order'],
    onSuccess: () => {
      notification.success({ message: 'Sync Order สำเร็จ' });
      refreshSync();
      refetch();
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const createOrder = useMutation({
    mutationFn: async (data: IBentoOrderRes[]) => await createOrders(data),
    mutationKey: ['create-order'],
    onSuccess: ({ data }) => {
      data.forEach((d: any) => {
        if (d.status === 'fulfilled') {
          notification.success({ message: 'Create Order สำเร็จ' });
          refreshSync();
          refetch();
        } else {
          notification.error({
            message: 'Create Order ไม่สำเร็จ',
            description: d.reason?.message,
          });
        }
      });
      refetch();
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const checkStockApi = useMutation({
    mutationFn: async (data: IBentoOrderRes[]) => await checkStocks(data),
    mutationKey: ['check stocks'],
    onSuccess: async ({ data }: { data: IBentoOrderRes[] }) => {
      const items = data.flatMap(o => {
        const order = o.ORDER_JSON as IOrderFull;
        return order.order_item.map((i, idx) => {
          const b: IBentoOrderItemCreate = {
            ...i,
            full_order_code: order.full_order_code,
            order_date: order.date_created,
            order_from: order.order_from,
            seq: idx + 1,
            order_billing: order.order_billing,
            ref_order: order,
          };
          return b;
        });
      });
      var have_stocks = items.every(
        c => c.have_master_stock && c.have_stock && c.have_stock_in_line,
      );
      if (have_stocks) {
        await createOrder.mutateAsync(data);
      } else {
        const insufficientStockItems = items.filter(
          item => !item.have_stock_in_line,
        );
        notification.error({
          message:
            'มีรายการที่สต็อกไม่เพียงพอ กรุณาตรวจสอบรายการที่มี (สต็อกไม่พอ)',
          description: (
            <div className="flex flex-col w-full">
              {insufficientStockItems.map((e, idx) => {
                return (
                  <div key={idx}>
                    <div className="font-semibold">
                      {idx + 1}: {e.full_order_code} {e.name}
                    </div>
                  </div>
                );
              })}
            </div>
          ),
        });

        const updatedItems = selected.map(item => {
          const insufficient = insufficientStockItems.find(
            i => i.order_id === item.order_id && i.seq === item.seq,
          );
          if (insufficient) {
            return {
              ...item,
              have_master_stock: insufficient.have_master_stock,
              have_stock: insufficient.have_stock,
              have_stock_in_line: insufficient.have_stock_in_line,
            };
          }
          return item;
        });
        setSelected(updatedItems);
        setSelectedRowKeys([]);
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  useEffect(() => {
    if (orders?.length) {
      const items = orders.flatMap(o => {
        const order = o.ORDER_JSON as IOrderFull;
        return order.order_item.map((i, idx) => {
          const b: IBentoOrderItemCreate = {
            ...i,
            full_order_code: order.full_order_code,
            order_date: order.date_created,
            order_from: order.order_from,
            seq: idx + 1,
            order_billing: order.order_billing,
            ref_order: order,
            have_master_stock: true,
            have_stock: true,
            have_stock_in_line: true,
          };
          return b;
        });
      });

      setSelected(items || []);
      setSelectedRowKeys([]);
    }
  }, [orders]);

  const handleSyncOrders = async () => {
    await sycnOrder.mutateAsync();
  };

  const handleCreateOrders = async () => {
    const selectedOrders = orders?.filter(c =>
      selectedRowKeys.includes(c.ORDER_ID as number),
    );
    await checkStockApi.mutateAsync(selectedOrders || []);
  };

  const columns = useMemo(
    () =>
      [
        {
          title: 'แหล่งขาย',
          dataIndex: 'order_from',
          className: 'fw-semibold',
          render: _ => getOrderFromName(_),
        },
        {
          title: 'เลขที่ออเดอร์',
          dataIndex: 'full_order_code',
          className: 'fw-semibold',
        },
        {
          title: 'วันที่ออเดอร์',
          dataIndex: 'order_date',
          className: 'fw-semibold',
          width: 120,
          align: 'center',
          render: _ => formatDate(_),
        },
        {
          title: 'รายการ',
          dataIndex: 'seq',
          className: 'fw-semibold',
          align: 'right',
          width: 40,
        },
        {
          title: 'สินค้า',
          dataIndex: 'product_id',
          className: 'fw-semibold',
        },
        {
          title: 'ชื่อสินค้า',
          dataIndex: 'name',
          className: 'fw-semibold',
          render: (_, rec) => (
            <div>{`${_} ${!rec.have_stock_in_line ? '(สต็อกไม่พอ)' : ''}`}</div>
          ),
        },
        {
          title: 'จำนวน',
          dataIndex: 'qty',
          align: 'right',
          className: 'fw-semibold',
          width: 100,
        },
        {
          title: 'มูลค่า',
          dataIndex: 'total',
          align: 'right',
          width: 100,
          className: 'fw-semibold',
          render: _ => formatNumber(_, 2),
        },
        {
          title: 'ใบกำกับภาษี',
          width: 100,
          align: 'center',
          dataIndex: 'order_billing',
          render: _ => (
            <div className={_ && 'fw-semibold'}>{(_ && 'YES') || ''}</div>
          ),
        },
      ] as TableColumnsType<IBentoOrderItemCreate>,
    [],
  );

  // useEffect(() => {
  //   const list = action === 'create' ? data?.data?.insert : data?.data?.update;
  //   setSelected(list || []);
  //   setSelectedRowKeys([]);
  // }, [action, data?.data?.insert, data?.data?.update]);

  const rowSelection = {
    onChange: (_: React.Key[], selectedRows: IBentoOrderItemCreate[]) => {
      setSelectedRowKeys(selectedRows.map(row => row.order_id));
    },
    selectedRowKeys,
    // getCheckboxProps: (record: IBentoOrderItemCreate) => ({
    //   disabled: !record.have_stock_in_line,
    // }),
  };

  const [searchText, setSearchText] = useState('');
  const onSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchText(value);
      }, 500),
    [],
  );

  // Filter selected data by searchText
  const filteredSelected = useMemo(() => {
    if (!searchText)
      return selected.map((i, idx) => {
        return i;
      });
    const lower = searchText.toLowerCase();
    return selected.filter(
      item =>
        item.full_order_code?.toLowerCase().includes(lower) ||
        item.category_name?.toLowerCase().includes(lower) ||
        item.name?.toLowerCase().includes(lower),
    );
  }, [selected, searchText]);

  return (
    <div>
      <Flex justify="space-between" gap={12} className="mb-3">
        <Flex align="start" gap={12}>
          <CustomSearch
            onSearch={function (text: string): void {
              onSearch(text);
            }}
          />
        </Flex>
        <Flex align="center" gap={12}>
          {formatDate(lastSync?.LAST_SYNC_TIME || '', dateTimeFormat)}
          <button
            type="button"
            className="btn btn-success"
            style={{ minWidth: 150 }}
            onClick={() => handleSyncOrders()}
          >
            {sycnOrder.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span className="sr-only">Loading...</span>
              </>
            ) : (
              'ค้นหาออเดอร์'
            )}
          </button>
        </Flex>
      </Flex>
      <Table<IBentoOrderItemCreate>
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        columns={columns}
        dataSource={filteredSelected}
        size="small"
        bordered
        pagination={false}
        locale={{ emptyText: 'ไม่พบรายการ' }}
        rowKey={'order_id'}
        summary={record => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell
                index={0}
                colSpan={8}
                className="fw-bold items-end"
                align="right"
              >
                TOTAL
              </Table.Summary.Cell>
              <Table.Summary.Cell
                index={7}
                colSpan={1}
                className="fw-bold items-end"
                align="right"
              >
                {formatNumber(Number(sumBy(record, 'total')), 2)}
              </Table.Summary.Cell>
              <Table.Summary.Cell
                index={0}
                colSpan={8}
                className="fw-bold items-end"
                align="center"
              ></Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
      <Flex justify="end" className="pt-4" gap={12}>
        <button
          type="button"
          className="btn btn-success"
          onClick={() => handleCreateOrders()}
          disabled={selectedRowKeys.length === 0}
        >
          {sycnOrder.isPending || createOrder.isPending ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              <span className="sr-only">Loading...</span>
            </>
          ) : (
            'บันทึกข้อมูลลงระบบ'
          )}
        </button>
      </Flex>
    </div>
  );
};

export default OrderPage;
